import {
    ImportedCompany,
    ImportedCompanyColumnMapping,
    ImportedCompanyEntityType,
    ImportedCompanyProcessingStatus,
    ImportedCompanySourceType,
    ImportedCompanyVisibility,
} from '@/types';

import { apiClient } from '../http';

export type ImportedEntityFinancialSection =
    | 'balance'
    | 'capital-structure'
    | 'cashflows'
    | 'holdings'
    | 'income'
    | 'multiples'
    | 'returns';

export type ImportedEntityPreview = {
    columnMappings: ImportedCompanyColumnMapping[];
    rows: Array<Record<string, unknown>>;
    sourceMetadata: Record<string, unknown>;
    summary: {
        columnCount: number;
        rowCount: number;
        sheetName: string;
    };
};

export type ImportedEntityCreateInput = {
    columnMappings?: ImportedCompanyColumnMapping[];
    data: Array<Record<string, unknown>>;
    description?: string;
    entityType: ImportedCompanyEntityType;
    name?: string;
    sourceMetadata?: Record<string, unknown>;
    sourceType?: ImportedCompanySourceType;
    visibility: ImportedCompanyVisibility;
    website?: string;
};

export type ImportedEntityDuplicatePolicy = 'create' | 'skip';
export type ImportedEntityReviewStatus = 'duplicate' | 'invalid' | 'ready';

export type StructuredImportedEntityInput = {
    columnMappings: ImportedCompanyColumnMapping[];
    data: Array<Record<string, unknown>>;
    duplicatePolicy: ImportedEntityDuplicatePolicy;
    enrichWithApollo: boolean;
    entityType: ImportedCompanyEntityType;
    financialImports?: ImportedEntityFinancialSyncInput[];
    sourceMetadata?: Record<string, unknown>;
    sourceType: ImportedCompanySourceType;
    visibility: ImportedCompanyVisibility;
};

export type ImportedEntityFinancialSyncInput = ImportedEntityFinancialsInput & {
    entityMatchColumn?: string;
    frequency: 'annual' | 'quarterly';
    periodColumn: string;
    section: ImportedEntityFinancialSection;
};

export type ImportedEntityReview = {
    items: Array<{
        dedupeKey?: string;
        existingEntityId?: string;
        name?: string;
        rowIndex: number;
        status: ImportedEntityReviewStatus;
        website?: string;
    }>;
    summary: Record<ImportedEntityReviewStatus | 'total', number>;
};

export type StructuredImportedEntityResult = {
    created: ImportedCompany[];
    importBatchId: string;
    review: ImportedEntityReview;
};

export type ImportedEntityFinancialsInput = {
    columnMappings?: ImportedCompanyColumnMapping[];
    data: Array<Record<string, unknown>>;
    sourceMetadata?: Record<string, unknown>;
};

export type ImportedEntityCapabilities = {
    canCreate: boolean;
    canManage: boolean;
    canRead: boolean;
    canUpdate: boolean;
};

export type ManagedImportedEntity = ImportedCompany & {
    financialSections: Array<{
        rowCount: number;
        section: ImportedEntityFinancialSection;
        updatedAt?: string;
    }>;
    rowCount: number;
};

export type ImportedEntityManagementParams = {
    cursor?: string;
    deleted?: 'active' | 'all' | 'deleted';
    entityType?: ImportedCompanyEntityType;
    limit?: number;
    processingStatus?: ImportedCompanyProcessingStatus;
    query?: string;
    sourceType?: ImportedCompanySourceType;
};

export type ImportedEntityManagementPage = {
    hasMore: boolean;
    items: ManagedImportedEntity[];
    nextCursor?: string;
    summary: {
        active: number;
        deleted: number;
        filtered: number;
    };
};

export type ImportedEntityUpdateInput = Partial<
    Pick<ImportedCompany, 'description' | 'entityType' | 'name' | 'visibility' | 'website'>
>;

export const importedEntityService = {
    capabilities: () => apiClient.get<ImportedEntityCapabilities>('/api/imported-entities/capabilities'),
    createBatch: (input: StructuredImportedEntityInput) =>
        apiClient.post<StructuredImportedEntityResult, StructuredImportedEntityInput>(
            '/api/imported-entities/bulk',
            input,
        ),
    create: (input: ImportedEntityCreateInput) =>
        apiClient.post<ImportedCompany, ImportedEntityCreateInput>('/api/imported-entities/upload', input),
    getById: (id: string) => apiClient.get<ImportedCompany>(`/api/imported-entities/${id}`),
    getManagedById: (id: string) => apiClient.get<ManagedImportedEntity>(`/api/imported-entities/management/${id}`),
    list: (params?: { entityType?: ImportedCompanyEntityType; query?: string }) =>
        apiClient.get<ImportedCompany[], typeof params>('/api/imported-entities', { params }),
    reviewBatch: (input: StructuredImportedEntityInput) =>
        apiClient.post<ImportedEntityReview, StructuredImportedEntityInput>(
            '/api/imported-entities/bulk/review',
            input,
        ),
    listManaged: (params?: ImportedEntityManagementParams) =>
        apiClient.get<ImportedEntityManagementPage, ImportedEntityManagementParams>(
            '/api/imported-entities/management',
            { params },
        ),
    remove: (id: string) => apiClient.delete<ManagedImportedEntity>(`/api/imported-entities/${id}`),
    restore: (id: string) => apiClient.post<ManagedImportedEntity>(`/api/imported-entities/${id}/restore`, {}),
    update: (id: string, input: ImportedEntityUpdateInput) =>
        apiClient.patch<ManagedImportedEntity, ImportedEntityUpdateInput>(`/api/imported-entities/${id}`, input),
    updateRow: (id: string, rowIndex: number, row: Record<string, unknown>) =>
        apiClient.patch<ManagedImportedEntity, { row: Record<string, unknown> }>(
            `/api/imported-entities/${id}/rows/${rowIndex}`,
            { row },
        ),
    updateFinancials: (id: string, section: ImportedEntityFinancialSection, input: ImportedEntityFinancialsInput) =>
        apiClient.put<ImportedCompany, ImportedEntityFinancialsInput>(
            `/api/imported-entities/${id}/financials/${section}`,
            input,
        ),
};
